import { type Tier, UNLIMITED_CREDITS } from '@/policy/limits';

export interface InitialSource {
  username?: string | null;
  email?: string | null;
}

// Cognito pools with email/phone sign-in aliases store a generated UUID as the
// username, which is meaningless for display. Detect that so we fall back to email.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLikelyUuid(s: string | null | undefined): boolean {
  return !!s && UUID_RE.test(s.trim());
}

// The real, human-friendly username — empty when we only have a UUID placeholder.
export function displayUsername(user: InitialSource | null): string {
  const uname = user?.username?.trim();
  return uname && !isLikelyUuid(uname) ? uname : '';
}

export function getInitial(user: InitialSource | null): string {
  if (!user) return '?';
  const source = displayUsername(user) || (user.email && user.email.trim()) || '';
  return source ? source.charAt(0).toUpperCase() : '?';
}

/**
 * The plan an account is on. 'free' is the tier of an account with no subscription, and since
 * 2026-09-29 there is no free plan for it to be on (no free credits, no free first app). "No
 * plan" is what it is, and still reads right beside the credits an older account kept.
 */
export function planLabel(tier: Tier): string {
  if (tier === 'unlimited') return 'Unlimited';
  return tier === 'paid' ? 'Pro plan' : 'No plan';
}

export function formatCredits(n: number): string {
  if (n >= UNLIMITED_CREDITS) return 'Unlimited';
  return `${n} ${n === 1 ? 'credit' : 'credits'}`;
}

/** The "plan · credits" subtitle, collapsed to just "Unlimited" for unlimited accounts. */
export function planSummary(tier: Tier, credits: number): string {
  if (tier === 'unlimited' || credits >= UNLIMITED_CREDITS) return 'Unlimited';
  return `${planLabel(tier)} · ${formatCredits(credits)}`;
}

/**
 * The Account row's second line for AI data sharing: when it was allowed, or that it was not.
 *
 * The date is the one recorded when Allow was tapped, in the reader's own format. A record whose
 * date cannot be read still says Allowed — the permission is what matters — with the stored
 * date as written rather than "Invalid Date".
 */
export function aiSharingSummary(record: { allowedAt: string } | null, locale?: string): string {
  if (!record) return 'Not allowed — Saddle asks before sending anything';
  const when = new Date(record.allowedAt);
  const date = Number.isNaN(when.getTime())
    ? record.allowedAt.slice(0, 10)
    : when.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  return `Allowed on ${date}`;
}

/*
 * ───────────────────────────── Redeem a code ─────────────────────────────
 *
 * Everything below is read by BOTH clients — the phone's Account screen and the web builder's
 * Account and Credits screens — so the same answer from `POST /redeem` reads the same way on
 * each. That is also why this file must stay pure: no IO, no state, no platform import (the web
 * may only import pure modules; web/src/__tests__/boundary.test.ts).
 */

/**
 * Whether this platform offers the Redeem code field at all.
 *
 * Not on iOS. App Store Review Guideline 3.1.1: "Apps may not use their own mechanisms to unlock
 * content or functionality, such as license keys … QR codes". A field that turns a typed code
 * into credits or a paid plan is the textbook case, and the reviewer's metered account would see
 * it on the first visit to Account.
 *
 * Hidden, not replaced with a pointer: 3.1.1 also forbids steering an iOS user to another way of
 * getting what the app sells, so the iOS app says nothing about where else a code works. The web
 * builder, which is outside the in-app-purchase surface, is where codes are redeemed.
 */
export function showsRedeemCode(platformOS: string): boolean {
  return platformOS !== 'ios';
}

/** The field's words, shared so the two clients name the same control the same way. */
export const REDEEM_WORDS = {
  /** The field's placeholder, and its accessible name — it has no visible label. */
  field: 'Enter a code',
  /** The button's visible text. */
  action: 'Redeem',
  /** The button's accessible name: "Redeem" alone does not say what is redeemed. */
  actionLabel: 'Redeem code',
  succeeded: 'Code redeemed',
  failed: 'Redeem failed',
} as const;

/**
 * Every way `POST /redeem` says no, as the server spells it (infra/lambda/handlers/llm.ts), plus
 * `rate_limited` for a 429 and `unknown` for anything else — a dropped connection included.
 */
export type RedeemRefusal =
  | 'invalid_code'
  | 'already_redeemed'
  | 'code_expired'
  | 'code_exhausted'
  | 'signup_required'
  | 'promo_unavailable'
  | 'rate_limited'
  | 'unknown';

const REFUSAL_MESSAGES: Record<RedeemRefusal, string> = {
  invalid_code: "That code isn't valid.",
  already_redeemed: "You've already redeemed this code.",
  code_expired: 'That code has expired.',
  code_exhausted: 'That code has been used up.',
  signup_required: 'Sign in to redeem a code.',
  /*
   * The server answers this when it cannot read its own table of codes. It is deliberately NOT
   * "invalid code": somebody holding a code they were given would take that as a typo and try
   * again forever, and nobody would ever report it. So the sentence says whose problem it is.
   */
  promo_unavailable: "Codes can't be redeemed right now. This is a problem on our side, not with your code — try again later.",
  rate_limited: 'Too many tries just now. Wait a minute and try again.',
  unknown: 'Could not redeem the code. Try again.',
};

/** The server's reason, or anything else, as one of the refusals this screen can name. */
export function redeemRefusal(raw: string | null | undefined): RedeemRefusal {
  const reason = (raw ?? '').trim();
  // Own keys only: `in` would also accept 'toString', and the message for it is a function.
  return Object.prototype.hasOwnProperty.call(REFUSAL_MESSAGES, reason) ? (reason as RedeemRefusal) : 'unknown';
}

export function redeemFailureMessage(raw: string | null | undefined): string {
  return REFUSAL_MESSAGES[redeemRefusal(raw)];
}

/**
 * What a successful redemption did, in one or two sentences.
 *
 * A code can grant credits, a plan, or both (the server's promo table: `credits`, `paid`,
 * `subscription`). The phone used to read only `granted`, so a code that granted a plan and no
 * credits answered "Your account is already unlimited." — to somebody who had just been given
 * Pro. The plan is named only when it CHANGED: the server reports the account's tier after the
 * code, which is 'paid' for someone already on Pro whatever the code gave.
 */
export function redeemSuccessMessage(
  result: { granted: number; tier?: string | null },
  previousTier?: string | null,
  locale?: string,
): string {
  if (result.tier === 'unlimited' || previousTier === 'unlimited') {
    return 'Your account is already unlimited.';
  }
  const parts: string[] = [];
  if (result.granted > 0) {
    const n = result.granted;
    parts.push(`${n.toLocaleString(locale)} ${n === 1 ? 'credit' : 'credits'} added to your account.`);
  }
  if (result.tier === 'paid' && previousTier !== 'paid') {
    parts.push(`You're now on the ${planLabel('paid')}.`);
  }
  return parts.length > 0 ? parts.join(' ') : 'Your account already has everything this code gives.';
}

/*
 * ───────────────────────────── Delete account ─────────────────────────────
 *
 * The warning both clients show before anything is deleted. Not "in the App Store": somebody who
 * subscribed on the web is billed by Stripe, and sending them to a page where they have no
 * subscription is how you end up still charging a person who did everything they were told to.
 */
export const DELETE_ACCOUNT_TITLE = 'Delete account';
export const DELETE_ACCOUNT_WARNING =
  'This permanently deletes your account and all your data, and can’t be undone.\n\n'
  + 'Deleting your account does NOT stop billing. If you have a subscription, cancel it first — '
  + 'in the App Store if you subscribed on your phone, or from your account on the website if you '
  + 'subscribed there.';
